import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Typography,
  Select,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined } from '@ant-design/icons';
import { useInspections } from '../../hooks/useQuality';
import type { InspectionRecord } from '../../services/quality.service';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

type Row = InspectionRecord & {
  parent_receiving_inspection?: { inspection_number?: string };
  grn?: { grn_number?: string };
};

const ReceivingDimensionalInspectionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { data: inspections = [], isLoading } = useInspections({
    inspection_category: 'Dimensional',
    source_document_type: 'receiving_inspection',
    ...(statusFilter ? { status: statusFilter } : {}),
  });

  const rows = inspections as Row[];

  const filtered = useMemo(() => {
    if (!searchText.trim()) return rows;
    const q = searchText.toLowerCase();
    return rows.filter(
      (i) =>
        i.inspection_number.toLowerCase().includes(q) ||
        String(i.observations || '')
          .toLowerCase()
          .includes(q) ||
        String((i as Row).grn?.grn_number || '')
          .toLowerCase()
          .includes(q)
    );
  }, [rows, searchText]);

  const columns: ColumnsType<Row> = [
    { title: 'Inspection No.', dataIndex: 'inspection_number', width: 140 },
    {
      title: 'Receiving',
      key: 'parent',
      width: 140,
      render: (_, r) =>
        r.parent_receiving_inspection?.inspection_number ||
        (r.source_document_id ? r.source_document_id.slice(0, 8) : '—'),
    },
    {
      title: 'Date',
      key: 'date',
      width: 120,
      render: (_, r) => {
        const d = r.scheduled_date || r.actual_date || r.created_at;
        return d ? dayjs(d).format('DD/MM/YYYY') : '—';
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (s: string) => <Tag>{s}</Tag>,
    },
    {
      title: 'Result',
      dataIndex: 'result',
      width: 100,
      render: (r: string) => (
        <Tag color={r === 'Pass' ? 'green' : r === 'Fail' ? 'red' : 'default'}>{r || 'Pending'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 90,
      render: (_, row) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => navigate(`/qms/receiving-dimensional-inspections/${row.id}`)}
        >
          Open
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginBottom: 4 }}>
        Receiving Dimension Inspections
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Linked from Receiving Inspection — simple Pass/Fail (no WO measurement grid)
      </Text>
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Search
            placeholder="Search inspection no..."
            allowClear
            style={{ width: 280 }}
            onSearch={setSearchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 140 }}
            value={statusFilter || undefined}
            onChange={(v) => setStatusFilter(v || '')}
          >
            <Option value="Scheduled">Scheduled</Option>
            <Option value="In Progress">In Progress</Option>
            <Option value="Completed">Completed</Option>
            <Option value="Cancelled">Cancelled</Option>
          </Select>
        </Space>
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={filtered}
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        />
      </Card>
    </div>
  );
};

export default ReceivingDimensionalInspectionsPage;
